import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import useQualityService from '../../app/services/hooks/useQualityService';
import { Quality, QualityFilters, defaultQualityFilters } from '../../app/models/Quality';
import QualityDataTable from './components/tables/QualityDataTable';
import QualityEdit from './components/QualityEdit';
import QualityCreate from './components/QualityCreate';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Calidades',
        url: '/qualities',
        isActive: true
    }
];

const Qualities = () => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { fetchingGetQualities, getQualities, deleteQuality } = useQualityService();
    const [qualities, setQualities] = useState<Quality[]>([]);
    const [qualityIdEdit, setQualityIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);
    const { navigationPage } = useNavigationPage();

    const [qualityFilter, setQualityFilter] = useState<QualityFilters>(defaultQualityFilters);

    useEffect(() => {
        reloadTable();
    }, [qualityFilter]);

    const getAllQualities = () => {
        getQualities(qualityFilter, {
            onSuccess: (response: ServiceResponse) => {
                setQualities(response.data.qualities);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (qualityId: number) => {
        setShowingEdit(true);
        setQualityIdEdit(qualityId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setQualityIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const reloadTable = () => {
        getAllQualities();
    };

    const destroy = (qualityId: number) => {
        const _text = 'Está a punto de eliminar la calidad #' + qualityId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando calidad...');
                        deleteQuality(qualityId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'La calidad no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Calidades" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nueva calidad" />}
                />
                <QualityDataTable
                    qualities={qualities}
                    edit={showEdit}
                    destroy={destroy}
                    loading={fetchingGetQualities}
                    filter={qualityFilter}
                    setFilter={setQualityFilter}
                    totalRows={totalRows}
                    callbackSuccess={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Calidad"
                    backdrop={true}
                    showFooter={false}
                >
                    <QualityCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Calidad"
                    backdrop={true}
                    showFooter={false}
                >
                    <QualityEdit
                        qualityId={qualityIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default Qualities;
